import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Table,
  Input,
} from 'reactstrap';
import ConfirmModal from '../../components/ConfirmModal';
import { facturasService } from '../../services/facturasService';
import { ventasService } from '../../services/ventasService';
import ConfigurarPrefacturaModal from './ConfigurarPrefacturaModal';

function FacturaDetalle({ factura, onClose, onCancel, onReintentar, onDescargarXML }) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [motivoCodigo, setMotivoCodigo] = useState('');
  const [folioSustitucion, setFolioSustitucion] = useState('');
  const [cancelando, setCancelando] = useState(false);

  // Catálogo de motivos de cancelación según SAT
  const MOTIVOS_CANCELACION = [
    { value: '01', label: '01 - Comprobante emitido con errores con relación' },
    { value: '02', label: '02 - Comprobante emitido con errores sin relación' },
    { value: '03', label: '03 - No se llevó a cabo la operación' },
    { value: '04', label: '04 - Operación nominativa relacionada en la factura global' },
  ];
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfigurarModal, setShowConfigurarModal] = useState(false);
  const [venta, setVenta] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [facturaActualizada, setFacturaActualizada] = useState(factura);
  const [timbrando, setTimbrando] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    // Cargar venta y cliente cuando se monta el componente
    cargarVentaYCliente();
  }, [facturaActualizada]);

  const cargarVentaYCliente = async () => {
    if (!facturaActualizada) return;
    
    try {
      const ventaId = facturaActualizada.VentaID || facturaActualizada.ventaID;
      if (!ventaId) return;

      const response = await ventasService.getById(ventaId);
      if (response.success && response.data) {
        const ventaData = response.data;
        setVenta(ventaData);
        // El cliente puede venir en la venta
        if (ventaData.Cliente || ventaData.cliente) {
          setCliente(ventaData.Cliente || ventaData.cliente);
        }
      }
    } catch (err) {
    }
  };

  const recargarFactura = async () => {
    if (!facturaActualizada) return;
    
    try {
      const facturaId = facturaActualizada.FacturaID || facturaActualizada.facturaID;
      const response = await facturasService.getById(facturaId);
      if (response.success && response.data) {
        setFacturaActualizada(response.data);
        setSuccess('Factura actualizada');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Error al recargar la factura');
    }
  };

  const getEstatusBadge = (estatus) => {
    const estatusLower = (estatus || '').toLowerCase();
    if (estatusLower === 'timbrada') {
      return <Badge color="success">Timbrada</Badge>;
    } else if (estatusLower === 'pendiente') {
      return <Badge color="warning">Pendiente</Badge>;
    } else if (estatusLower === 'borrador') {
      return <Badge color="info">Borrador</Badge>;
    } else if (estatusLower === 'cancelada') {
      return <Badge color="danger">Cancelada</Badge>;
    } else if (estatusLower === 'error') {
      return <Badge color="danger">Error</Badge>;
    }
    return <Badge color="secondary">{estatus || 'Desconocido'}</Badge>;
  };

  const getUsoCFDILabel = (value) => {
    const usos = [
      { value: 'G01', label: 'G01 - Adquisición de mercancías' },
      { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
      { value: 'G03', label: 'G03 - Gastos en general' },
      { value: 'I01', label: 'I01 - Construcciones' },
      { value: 'I02', label: 'I02 - Mobilario y equipo de oficina por inversiones' },
      { value: 'I03', label: 'I03 - Equipo de transporte' },
      { value: 'I04', label: 'I04 - Equipo de computo y accesorios' },
      { value: 'I05', label: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
      { value: 'I06', label: 'I06 - Comunicaciones telefónicas' },
      { value: 'I07', label: 'I07 - Comunicaciones satelitales' },
      { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
      { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
      { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
      { value: 'D03', label: 'D03 - Gastos funerales' },
      { value: 'D04', label: 'D04 - Donativos' },
      { value: 'D05', label: 'D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)' },
      { value: 'D06', label: 'D06 - Aportaciones voluntarias al SAR' },
      { value: 'D07', label: 'D07 - Primas por seguros de gastos médicos' },
      { value: 'D08', label: 'D08 - Gastos de transportación escolar obligatoria' },
      { value: 'D09', label: 'D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones' },
      { value: 'D10', label: 'D10 - Pagos por servicios educativos (colegiaturas)' },
      { value: 'P01', label: 'P01 - Por definir' },
    ];
    const uso = usos.find(u => u.value === value);
    return uso ? uso.label : value;
  };

  const getFormaPagoLabel = (value) => {
    const formas = [
      { value: '01', label: '01 - Efectivo' },
      { value: '02', label: '02 - Cheque nominativo' },
      { value: '03', label: '03 - Transferencia electrónica de fondos' },
      { value: '04', label: '04 - Tarjeta de crédito' },
      { value: '05', label: '05 - Monedero electrónico' },
      { value: '06', label: '06 - Dinero electrónico' },
      { value: '08', label: '08 - Vales de despensa' },
      { value: '12', label: '12 - Dación en pago' },
      { value: '13', label: '13 - Pago por subrogación' },
      { value: '14', label: '14 - Pago por consignación' },
      { value: '15', label: '15 - Condonación' },
      { value: '17', label: '17 - Compensación' },
      { value: '23', label: '23 - Novación' },
      { value: '24', label: '24 - Confusión' },
      { value: '25', label: '25 - Remisión de deuda' },
      { value: '26', label: '26 - Prescripción o caducidad' },
      { value: '27', label: '27 - A satisfacción del acreedor' },
      { value: '28', label: '28 - Tarjeta de débito' },
      { value: '29', label: '29 - Tarjeta de servicios' },
      { value: '30', label: '30 - Aplicación de anticipos' },
      { value: '31', label: '31 - Intermediario pagos' },
      { value: '99', label: '99 - Por definir' },
    ];
    const forma = formas.find(f => f.value === value);
    return forma ? forma.label : value;
  };

  const getMetodoPagoLabel = (value) => {
    const metodos = [
      { value: 'PUE', label: 'PUE - Pago en una sola exhibición' },
      { value: 'PPD', label: 'PPD - Pago en parcialidades o diferido' },
    ];
    const metodo = metodos.find(m => m.value === value);
    return metodo ? metodo.label : value;
  };

  const getTipoComprobanteLabel = (value) => {
    const tipos = [
      { value: 'I', label: 'I - Ingreso' },
      { value: 'E', label: 'E - Egreso' },
      { value: 'T', label: 'T - Traslado' },
      { value: 'N', label: 'N - Nómina' },
      { value: 'P', label: 'P - Pago' },
    ];
    const tipo = tipos.find(t => t.value === value);
    return tipo ? tipo.label : value;
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!motivoCodigo) {
      setError('El motivo de cancelación es requerido');
      return;
    }

    // Si el motivo es 01, el folio de sustitución es obligatorio
    if (motivoCodigo === '01' && !folioSustitucion.trim()) {
      setError('El folio de factura sustituta es requerido cuando el motivo es 01');
      return;
    }

    try {
      setCancelando(true);
      const facturaId = factura.FacturaID || factura.facturaID;

      const cancelacionData = {
        MotivoCodigo: motivoCodigo,
      };

      // Solo incluir folio de sustitución si tiene valor
      if (folioSustitucion.trim()) {
        cancelacionData.FolioSustitucion = folioSustitucion.trim();
      }

      const response = await facturasService.cancelar(facturaId, cancelacionData);

      if (response.success) {
        setSuccess('Factura cancelada exitosamente');
        // Recargar la factura para obtener los datos de cancelación actualizados
        await recargarFactura();
        setTimeout(() => {
          setSuccess('');
          setShowCancelModal(false);
          setMotivoCodigo('');
          setFolioSustitucion('');
        }, 2000);
      } else {
        setError(response.Message || response.message || 'Error al cancelar la factura');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setCancelando(false);
    }
  };

  const handleDescargarXML = async () => {
    try {
      const response = await facturasService.descargarXML(factura.FacturaID || factura.facturaID);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${factura.FacturaID || factura.facturaID}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('XML descargado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al descargar el XML');
    }
  };

  const handleDescargarPDF = async () => {
    try {
      const response = await facturasService.descargarPDF(factura.FacturaID || factura.facturaID);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${factura.FacturaID || factura.facturaID}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('PDF descargado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al descargar el PDF');
    }
  };

  const handleDescargarAcuseCancelacion = async () => {
    try {
      const response = await facturasService.descargarAcuseCancelacionXML(factura.FacturaID || factura.facturaID);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `acuse_cancelacion_factura_${factura.FacturaID || factura.facturaID}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('Acuse de cancelación descargado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al descargar el acuse de cancelación');
    }
  };

  const handleReintentar = async () => {
    try {
      const response = await facturasService.reintentarTimbrado(factura.FacturaID || factura.facturaID);
      if (response.success) {
        setSuccess('Factura marcada para reintentar timbrado');
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 2000);
      } else {
        setError(response.Message || response.message || 'Error al reintentar timbrado');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    }
  };

  const handleTimbrar = async () => {
    if (!facturaActualizada) {
      setError('No se puede timbrar la factura');
      return;
    }

    setError('');
    setSuccess('');

    try {
      setTimbrando(true);
      const facturaId = facturaActualizada.FacturaID || facturaActualizada.facturaID;
      const response = await facturasService.timbrarPrefactura(facturaId);

      if (response.success) {
        setSuccess('Factura timbrada exitosamente');
        // Recargar la factura para mostrar los nuevos datos
        await recargarFactura();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.Message || response.message || 'Error al timbrar la factura');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setTimbrando(false);
    }
  };

  // Normalizar datos
  const facturaNormalizada = {
    FacturaID: facturaActualizada?.FacturaID || facturaActualizada?.facturaID || factura?.FacturaID || factura?.facturaID,
    VentaID: facturaActualizada?.VentaID || facturaActualizada?.ventaID || factura?.VentaID || factura?.ventaID,
    VentaFolio: facturaActualizada?.VentaFolio || facturaActualizada?.ventaFolio || factura?.VentaFolio || factura?.ventaFolio,
    ClienteID: facturaActualizada?.ClienteID || facturaActualizada?.clienteID || factura?.ClienteID || factura?.clienteID,
    ClienteNombre: facturaActualizada?.ClienteNombre || facturaActualizada?.clienteNombre || factura?.ClienteNombre || factura?.clienteNombre || '',
    ClienteRFC: facturaActualizada?.ClienteRFC || facturaActualizada?.clienteRFC || factura?.ClienteRFC || factura?.clienteRFC,
    UUID: facturaActualizada?.UUID || facturaActualizada?.uuid || factura?.UUID || factura?.uuid,
    FolioFiscal: facturaActualizada?.FolioFiscal || facturaActualizada?.folioFiscal || factura?.FolioFiscal || factura?.folioFiscal,
    Serie: facturaActualizada?.Serie || facturaActualizada?.serie || factura?.Serie || factura?.serie,
    Estatus: facturaActualizada?.Estatus || facturaActualizada?.estatus || factura?.Estatus || factura?.estatus || '',
    FechaTimbrado: facturaActualizada?.FechaTimbrado || facturaActualizada?.fechaTimbrado || factura?.FechaTimbrado || factura?.fechaTimbrado,
    ErrorTimbrado: facturaActualizada?.ErrorTimbrado || facturaActualizada?.errorTimbrado || factura?.ErrorTimbrado || factura?.errorTimbrado,
    FechaCreacion: facturaActualizada?.FechaCreacion || facturaActualizada?.fechaCreacion || factura?.FechaCreacion || factura?.fechaCreacion,
    FechaModificacion: facturaActualizada?.FechaModificacion || facturaActualizada?.fechaModificacion || factura?.FechaModificacion || factura?.fechaModificacion,
    UsoCFDI: facturaActualizada?.UsoCFDI || facturaActualizada?.usoCFDI || factura?.UsoCFDI || factura?.usoCFDI,
    FormaPago: facturaActualizada?.FormaPago || facturaActualizada?.formaPago || factura?.FormaPago || factura?.formaPago,
    MetodoPago: facturaActualizada?.MetodoPago || facturaActualizada?.metodoPago || factura?.MetodoPago || factura?.metodoPago,
    TipoDeComprobante: facturaActualizada?.TipoDeComprobante || facturaActualizada?.tipoDeComprobante || factura?.TipoDeComprobante || factura?.tipoDeComprobante,
    CondicionesDePago: facturaActualizada?.CondicionesDePago || facturaActualizada?.condicionesDePago || factura?.CondicionesDePago || factura?.condicionesDePago,
    TieneAcuseCancelacion: facturaActualizada?.TieneAcuseCancelacion || facturaActualizada?.tieneAcuseCancelacion || factura?.TieneAcuseCancelacion || factura?.tieneAcuseCancelacion || false,
    CancelacionFacturaID: facturaActualizada?.CancelacionFacturaID || facturaActualizada?.cancelacionFacturaID || factura?.CancelacionFacturaID || factura?.cancelacionFacturaID,
    MotivoCancelacion: facturaActualizada?.MotivoCancelacion || facturaActualizada?.motivoCancelacion || factura?.MotivoCancelacion || factura?.motivoCancelacion,
    MotivoCodigoCancelacion: facturaActualizada?.MotivoCodigoCancelacion || facturaActualizada?.motivoCodigoCancelacion || factura?.MotivoCodigoCancelacion || factura?.motivoCodigoCancelacion,
    FechaCancelacion: facturaActualizada?.FechaCancelacion || facturaActualizada?.fechaCancelacion || factura?.FechaCancelacion || factura?.fechaCancelacion,
  };

  const estatusLower = facturaNormalizada.Estatus.toLowerCase();
  const puedeCancelar = estatusLower === 'timbrada';
  const puedeReintentar = estatusLower === 'error' || estatusLower === 'pendiente';
  const puedeDescargarXML = estatusLower === 'timbrada' && facturaNormalizada.UUID;
  const puedeDescargarPDF = estatusLower === 'timbrada' && facturaNormalizada.UUID;
  const puedeEditar = estatusLower === 'borrador' || estatusLower === 'pendiente';
  const puedeTimbrar = estatusLower === 'borrador' || estatusLower === 'pendiente';
  // Mostrar botón de acuse si la factura está cancelada y tiene acuse disponible
  const puedeDescargarAcuse = estatusLower === 'cancelada' && facturaNormalizada.TieneAcuseCancelacion;

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Detalle de Factura #{facturaNormalizada.FacturaID}</CardTitle>
                </Col>
                <Col className="text-right">
                  {puedeEditar && (
                    <Button color="warning" onClick={() => setShowConfigurarModal(true)} className="mr-2">
                      <i className="tim-icons icon-settings" /> <span className="d-none d-md-inline">Editar Prefactura</span>
                    </Button>
                  )}
                  {puedeTimbrar && (
                    <Button color="success" onClick={handleTimbrar} className="mr-2" disabled={timbrando}>
                      <i className="tim-icons icon-check-2" /> <span className="d-none d-md-inline">{timbrando ? 'Timbrando...' : 'Timbrar Factura'}</span>
                    </Button>
                  )}
                  {puedeReintentar && (
                    <Button color="warning" onClick={handleReintentar} className="mr-2">
                      <i className="tim-icons icon-refresh-02" /> <span className="d-none d-md-inline">Reintentar Timbrado</span>
                    </Button>
                  )}
                  {puedeCancelar && (
                    <Button color="danger" onClick={handleCancel} className="mr-2">
                      <i className="tim-icons icon-simple-remove" /> <span className="d-none d-md-inline">Cancelar Factura</span>
                    </Button>
                  )}
                  {puedeDescargarXML && (
                    <Button color="success" onClick={handleDescargarXML} className="mr-2">
                      <i className="fas fa-file-code" /> <span className="d-none d-md-inline">Descargar XML</span>
                    </Button>
                  )}
                  {puedeDescargarPDF && (
                    <Button color="info" onClick={handleDescargarPDF} className="mr-2">
                      <i className="fas fa-file-pdf" /> <span className="d-none d-md-inline">Descargar PDF</span>
                    </Button>
                  )}
                  {puedeDescargarAcuse && (
                    <Button color="warning" onClick={handleDescargarAcuseCancelacion} className="mr-2">
                      <i className="fas fa-file-code" /> <span className="d-none d-md-inline">Descargar Acuse Cancelación (XML)</span>
                    </Button>
                  )}
                  <Button color="secondary" onClick={onClose}>
                    <i className="tim-icons icon-simple-remove" /> <span className="d-none d-md-inline">Cerrar</span>
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <Row className="mb-4">
                <Col md="6">
                  <h6>Información General</h6>
                  <Table borderless size="sm">
                    <tbody>
                      <tr>
                        <td><strong>Venta:</strong></td>
                        <td>{facturaNormalizada.VentaFolio || `#${facturaNormalizada.VentaID}`}</td>
                      </tr>
                      <tr>
                        <td><strong>Cliente:</strong></td>
                        <td>{facturaNormalizada.ClienteNombre}</td>
                      </tr>
                      <tr>
                        <td><strong>RFC:</strong></td>
                        <td>{facturaNormalizada.ClienteRFC || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Serie:</strong></td>
                        <td>{facturaNormalizada.Serie || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Folio Fiscal:</strong></td>
                        <td>{facturaNormalizada.FolioFiscal || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>UUID:</strong></td>
                        <td>
                          {facturaNormalizada.UUID ? (
                            <span style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                              {facturaNormalizada.UUID}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Estatus:</strong></td>
                        <td>{getEstatusBadge(facturaNormalizada.Estatus)}</td>
                      </tr>
                      <tr>
                        <td><strong>Fecha Creación:</strong></td>
                        <td>{formatDate(facturaNormalizada.FechaCreacion)}</td>
                      </tr>
                      {facturaNormalizada.FechaTimbrado && (
                        <tr>
                          <td><strong>Fecha Timbrado:</strong></td>
                          <td>{formatDate(facturaNormalizada.FechaTimbrado)}</td>
                        </tr>
                      )}
                      {facturaNormalizada.FechaCancelacion && (
                        <tr>
                          <td><strong>Fecha Cancelación:</strong></td>
                          <td>{formatDate(facturaNormalizada.FechaCancelacion)}</td>
                        </tr>
                      )}
                      {facturaNormalizada.MotivoCancelacion && (
                        <tr>
                          <td><strong>Motivo Cancelación:</strong></td>
                          <td>
                            {facturaNormalizada.MotivoCodigoCancelacion && (
                              <Badge color="warning" className="mr-2">
                                {facturaNormalizada.MotivoCodigoCancelacion}
                              </Badge>
                            )}
                            {facturaNormalizada.MotivoCancelacion}
                          </td>
                        </tr>
                      )}
                      {facturaNormalizada.ErrorTimbrado && (
                        <tr>
                          <td><strong>Error Timbrado:</strong></td>
                          <td>
                            <Alert color="danger" className="mb-0 p-2">
                              {facturaNormalizada.ErrorTimbrado}
                            </Alert>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              {/* Información de Prefactura (solo si existe) */}
              {(facturaNormalizada.UsoCFDI || facturaNormalizada.FormaPago || facturaNormalizada.MetodoPago || facturaNormalizada.TipoDeComprobante) && (
                <Row className="mb-4">
                  <Col md="6">
                    <h6>Información de Prefactura (CFDI)</h6>
                    <Table borderless size="sm">
                      <tbody>
                        {facturaNormalizada.TipoDeComprobante && (
                          <tr>
                            <td><strong>Tipo de Comprobante:</strong></td>
                            <td>{getTipoComprobanteLabel(facturaNormalizada.TipoDeComprobante)}</td>
                          </tr>
                        )}
                        {facturaNormalizada.UsoCFDI && (
                          <tr>
                            <td><strong>Uso CFDI:</strong></td>
                            <td>{getUsoCFDILabel(facturaNormalizada.UsoCFDI)}</td>
                          </tr>
                        )}
                        {facturaNormalizada.FormaPago && (
                          <tr>
                            <td><strong>Forma de Pago:</strong></td>
                            <td>{getFormaPagoLabel(facturaNormalizada.FormaPago)}</td>
                          </tr>
                        )}
                        {facturaNormalizada.MetodoPago && (
                          <tr>
                            <td><strong>Método de Pago:</strong></td>
                            <td>{getMetodoPagoLabel(facturaNormalizada.MetodoPago)}</td>
                          </tr>
                        )}
                        {facturaNormalizada.CondicionesDePago && (
                          <tr>
                            <td><strong>Condiciones de Pago:</strong></td>
                            <td>{facturaNormalizada.CondicionesDePago}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showCancelModal}
        toggle={() => {
          setShowCancelModal(false);
          setMotivoCodigo('');
          setFolioSustitucion('');
          setError('');
        }}
        title="Cancelar Factura"
        message={
          <div>
            <p>¿Está seguro de cancelar esta factura?</p>
            <div className="mt-3">
              <label className="form-label">Motivo de Cancelación (SAT) *</label>
              <Input
                type="select"
                value={motivoCodigo}
                onChange={(e) => {
                  setMotivoCodigo(e.target.value);
                  // Si cambia a un motivo diferente de 01, limpiar folio de sustitución
                  if (e.target.value !== '01') {
                    setFolioSustitucion('');
                  }
                }}
                required
              >
                <option value="">Seleccione un motivo</option>
                {MOTIVOS_CANCELACION.map((motivo) => (
                  <option key={motivo.value} value={motivo.value}>
                    {motivo.label}
                  </option>
                ))}
              </Input>
            </div>
            {motivoCodigo === '01' && (
              <div className="mt-3">
                <label className="form-label">Folio de Factura Sustituta *</label>
                <Input
                  type="text"
                  placeholder="Ingrese el folio de la factura que sustituye a esta"
                  value={folioSustitucion}
                  onChange={(e) => setFolioSustitucion(e.target.value)}
                  required
                />
                <small className="text-muted">
                  Requerido cuando el motivo es 01 (Comprobante emitido con errores con relación)
                </small>
              </div>
            )}
          </div>
        }
        confirmText="Cancelar Factura"
        cancelText="Cerrar"
        onConfirm={confirmCancel}
        loading={cancelando}
      />

      <ConfigurarPrefacturaModal
        isOpen={showConfigurarModal}
        toggle={() => setShowConfigurarModal(false)}
        venta={venta}
        cliente={cliente}
        onSuccess={() => {
          setShowConfigurarModal(false);
          recargarFactura();
        }}
      />
    </div>
  );
}

export default FacturaDetalle;

